type DefineFn = {
    (deps: Array<unknown>, moduleRoot: unknown): void;
    amd?: Record<string, unknown>;
};

class Amd {
    private defineBuffor: Array<unknown> = [];
    private results: Map<string, Promise<unknown>>;
    private activeImports: number = 0;

    public constructor() {
        this.results = new Map();
    }

    public define = (deps: Array<unknown>, moduleRoot: unknown): void => {
        if (deps.length > 0) {
            console.error('AMD define dependencies, the module was expected to have no dependencies');
        } else {
            if (typeof moduleRoot === 'function') {
                this.defineBuffor.push(moduleRoot());
            } else {
                console.error('AMD define - Expected function');
            }
        }
    };

    private installDefine = (): void => {
        if (typeof window === 'undefined') {
            return;
        }

        this.activeImports += 1;
        if (this.activeImports > 1) {
            return;
        }

        const defineFn: DefineFn = (deps: Array<unknown>, moduleRoot: unknown): void => {
            this.define(deps, moduleRoot);
        };
        defineFn.amd = {};

        //@ts-expect-error - host AMD shim for Paysafe / socket.io
        window.define = defineFn;
    };

    private uninstallDefine = (): void => {
        if (typeof window === 'undefined') {
            return;
        }

        this.activeImports = Math.max(0, this.activeImports - 1);
        if (this.activeImports > 0) {
            return;
        }

        try {
            //@ts-expect-error - remove temporary AMD shim
            delete window.define;
        } catch {
            //@ts-expect-error
            window.define = undefined;
        }
    };

    public importAmd = (path: string): Promise<unknown> => {
        const result = this.results.get(path);

        if (result !== undefined) {
            return result;
        }

        this.defineBuffor = [];
        this.installDefine();

        const newResult = new Promise((resolve, reject) => {
            let isLoading = true;
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = path;

            const finish = (onDone: () => void): void => {
                if (isLoading === false) {
                    return;
                }

                isLoading = false;
                this.uninstallDefine();
                onDone();
            };

            const callback = (): void => {
                finish(() => {
                    const defined = this.defineBuffor.pop();
                    if (this.defineBuffor.length !== 0) {
                        console.error('AMD import - the define function was expected to be called once');
                    }

                    resolve(defined);
                    this.defineBuffor = [];
                });
            };

            const error = (): void => {
                finish(() => {
                    reject();
                });
            };

            script.addEventListener('load', callback);
            script.onload = callback;
            script.addEventListener('error', error);
            script.onerror = error;

            document.body.appendChild(script);
        });

        this.results.set(path, newResult);
        return newResult;
    };
}

const amd = new Amd();

export const importAmd = (path: string): Promise<unknown> => {
    return amd.importAmd(path);
};
